import {
  CloseOutlined,
  HubOutlined,
  LockOutlined,
  SaveOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import useStyles from "./styles";

// Mesmas regras do backend (upsertErpConfig) para dar retorno antes de salvar.
const validarCampo = (campo, valor, secretoDefinido) => {
  const texto = campo.secreto ? valor : valor.trim();
  if (!texto) {
    return campo.obrigatorio && !secretoDefinido ? "Campo obrigatório" : "";
  }
  if (campo.tipo === "number" && !/^\d+$/.test(texto))
    return "Informe apenas números";
  if (campo.tipo === "url" && !/^https?:\/\/\S+$/i.test(texto)) {
    return "Deve começar com http:// ou https://";
  }
  return "";
};

// Agrupa os campos pelo `grupo` do catálogo, mantendo a ordem original.
const agrupar = (campos = []) =>
  campos.reduce((grupos, campo) => {
    const nome = campo.grupo || "";
    const atual = grupos.find((g) => g.nome === nome);
    if (atual) atual.campos.push(campo);
    else grupos.push({ nome, campos: [campo] });
    return grupos;
  }, []);

export default function ConexaoFormModal({
  open,
  erp,
  categoria,
  definicao,
  conexao,
  loading,
  onClose,
  onSave,
}) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const [descricao, setDescricao] = useState("");
  const [valores, setValores] = useState({});
  const [visiveis, setVisiveis] = useState({});
  const [tentouSalvar, setTentouSalvar] = useState(false);

  const campos = definicao?.campos || [];

  useEffect(() => {
    if (!open) return;
    setTentouSalvar(false);
    setVisiveis({});
    setDescricao(conexao?.descricao || "");
    setValores(
      Object.fromEntries(
        campos.map((c) => [
          c.chave,
          c.secreto ? "" : conexao?.valores?.[c.chave] || "",
        ]),
      ),
    );
  }, [open, conexao, definicao]);

  const erros = Object.fromEntries(
    campos.map((c) => [
      c.chave,
      validarCampo(
        c,
        valores[c.chave] || "",
        conexao?.secretosDefinidos?.[c.chave],
      ),
    ]),
  );
  const valido = Object.values(erros).every((e) => !e);

  const handleSalvar = () => {
    setTentouSalvar(true);
    if (!valido) return;
    onSave({ id: conexao?.id, erpId: erp.id, categoria, descricao, valores });
  };

  const renderCampo = (campo) => {
    const erro = tentouSalvar && erros[campo.chave];
    const mantido = campo.secreto && conexao?.secretosDefinidos?.[campo.chave];
    const visivel = visiveis[campo.chave];
    return (
      <TextField
        key={campo.chave}
        label={campo.label}
        required={campo.obrigatorio && !mantido}
        type={campo.secreto && !visivel ? "password" : "text"}
        placeholder={
          mantido ? "•••••••• (valor atual mantido)" : campo.placeholder
        }
        value={valores[campo.chave] || ""}
        onChange={(e) =>
          setValores({ ...valores, [campo.chave]: e.target.value })
        }
        disabled={loading}
        error={!!erro}
        size="small"
        fullWidth
        autoComplete={campo.secreto ? "new-password" : "off"}
        sx={campo.tipo === "url" ? styles.campoLargo : undefined}
        InputProps={
          campo.secreto
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      tabIndex={-1}
                      onClick={() =>
                        setVisiveis({ ...visiveis, [campo.chave]: !visivel })
                      }
                    >
                      {visivel ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }
            : undefined
        }
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: styles.dialogPaper }}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={styles.titulo}>
            {conexao
              ? `Editar conexão de ${definicao?.titulo} #${conexao.posicao}`
              : `Nova conexão de ${definicao?.titulo}`}
          </Typography>
          <Box sx={styles.erpRow}>
            <Typography sx={styles.erpLabel}>ERP:</Typography>
            <Chip
              icon={<HubOutlined />}
              label={`${erp?.nome} · ${erp?.tipo}`}
              size="small"
              sx={styles.chipErp}
            />
          </Box>
        </Box>
        <IconButton onClick={onClose} disabled={loading} size="small">
          <CloseOutlined fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={styles.content}>
        <TextField
          label="Identificação"
          placeholder={
            categoria === "BANCO"
              ? "Ex.: Banco principal, Réplica de leitura"
              : "Ex.: API de produção"
          }
          helperText="Nome para reconhecer esta conexão na lista (opcional)"
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          disabled={loading}
          size="small"
          fullWidth
        />

        {agrupar(campos).map((grupo) => (
          <Box key={grupo.nome || "campos"} sx={styles.grupo}>
            {grupo.nome && (
              <Typography sx={styles.grupoTitulo}>{grupo.nome}</Typography>
            )}
            <Box sx={styles.grid}>{grupo.campos.map(renderCampo)}</Box>
          </Box>
        ))}
      </DialogContent>

      <DialogActions sx={styles.actions}>
        <Box sx={styles.aviso}>
          <LockOutlined sx={styles.avisoIcone} />
          <Typography sx={styles.avisoTexto}>
            Os valores são criptografados antes de serem salvos.
          </Typography>
        </Box>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSalvar}
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveOutlined />
            )
          }
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
